<script lang="ts" setup>
import type { CartItemType } from '~/api/cart';
import placeholder from '~/assets/icons/placeholder.svg?url';

const MODAL_KEY = 'order-cart-modal';

const { t } = useI18n();
const commonStore = useCommonStore();
const productCartStore = useProductCartStore();
const subscribeProductsStore = useSubscribeProductsStore();
const modalStore = useModalStore();
const orderStore = useOrderStore();

const isInfoModalVisible = ref(false);

const baseFormattedPrice = computed(() =>
  priceFormat(
    orderStore.summary.basketPrice + orderStore.summary.discountPrice,
  ),
);
const deliveryFormattedPrice = computed(() =>
  priceFormat(orderStore.summary.deliveryPrice),
);
const discountFormattedPrice = computed(() =>
  priceFormat(orderStore.summary.discountPrice),
);
const totalFormattedPrice = computed(() =>
  priceFormat(orderStore.summary.total),
);

const getProductPicture = (productId: number) =>
  commonStore.products.find((item) => item.id === productId)?.picture ||
  placeholder;

const updateCartItem = (item: CartItemType, quantity: number) =>
  orderStore.updateCartItem(item, quantity);

const onDeleteItem = (item: CartItemType) => {
  modalStore.showConfirmation({
    title: t('delete_product_confirmation'),
    accept: () => orderStore.deleteCartItem(item),
  });
};

const onDeleteSubscribeItem = (productId: number) => {
  modalStore.showConfirmation({
    title: t('delete_subscription_confirmation'),
    accept: () => subscribeProductsStore.onDelete(productId),
  });
};

onUnmounted(() => {
  modalStore.closeModal(MODAL_KEY);
});
</script>

<template>
  <transition name="overlay">
    <div
      v-if="modalStore.modalKeys[MODAL_KEY]"
      @click="modalStore.closeModal(MODAL_KEY)"
      class="orderProductCart__overlay"
    />
  </transition>

  <div
    class="orderProductCart"
    :class="{ orderProductCart_visible: modalStore.modalKeys[MODAL_KEY] }"
    v-bind="$attrs"
  >
    <button
      @click="modalStore.toggleModal(MODAL_KEY)"
      class="orderProductCart__controller"
    >
      <span>{{ t('header') }}</span>
      <span>{{ totalFormattedPrice }}</span>
    </button>

    <div class="orderProductCart__content">
      <div class="orderProductCart__header">
        <span class="orderProductCart__title">{{ t('title') }}</span>
        <span>
          {{ t('products', { n: productCartStore.totalCountAvailable }) }}
        </span>
      </div>

      <BaseToggleTransition
        :state="productCartStore.initialized ? 'loading' : 'active'"
      >
        <BaseLoader v-if="!productCartStore.initialized" dark area />
        <div
          v-else-if="orderStore.cartData.length"
          class="orderProductCart__list"
        >
          <ProductCartItem
            v-for="item in orderStore.cartData"
            @update:count="updateCartItem(item, $event)"
            @delete="onDeleteItem(item)"
            :key="item.skuId"
            :name="item.name"
            :code="item.code"
            :picture="getProductPicture(item.productId)"
            :price="item.price"
            :base-price="item.basePrice"
            :count="item.quantity"
            :is-available="item.isAvailable"
            :is-gift="item.isGift"
          />
        </div>
        <p v-else class="orderProductCart__empty">
          {{ t('empty') }}
        </p>
      </BaseToggleTransition>

      <div class="orderProductCart__info">
        <div class="orderProductCart__row">
          <span>{{ t('cell.price') }}</span>
          <span>{{ baseFormattedPrice }}</span>
        </div>
        <div
          v-if="orderStore.summary.discountPrice"
          class="orderProductCart__row"
        >
          <span>{{ t('cell.discount') }}</span>
          <span>
            {{
              t('cell.formatted_discount', { value: discountFormattedPrice })
            }}
          </span>
        </div>
        <div class="orderProductCart__row">
          <span>{{ t('cell.delivery') }}</span>
          <span>{{ deliveryFormattedPrice }}</span>
        </div>
      </div>

      <div class="orderProductCart__total">
        <span>{{ t('cell.total') }}</span>
        <span>{{ totalFormattedPrice }}</span>
      </div>

      <BaseCollapse
        v-if="subscribeProductsStore.products.length"
        visible
        class="orderProductCart__subscribe"
      >
        <div class="orderProductCart__header">
          <span
            class="orderProductCart__title orderProductCart__title_subscribe"
          >
            {{
              t('subscription_benefit', {
                discount: SUBSCRIPTION_DISCOUNT_VALUE,
              })
            }}
          </span>
          <button
            @click="isInfoModalVisible = true"
            class="orderProductCart__question"
          >
            {{ t('question') }}
          </button>
        </div>

        <div class="orderProductCart__list orderProductCart__list_subscribe">
          <ProductCartItem
            v-for="item in subscribeProductsStore.products"
            @delete="onDeleteSubscribeItem(item.id)"
            :key="item.id"
            :name="item.name"
            :code="item.code"
            :picture="item.picture || placeholder"
            :price="
              calculateSubscribeProductPrice(
                item.priceInfo.basePrice || item.priceInfo.price,
              )
            "
            :base-price="item.priceInfo.price"
            :additional="
              subscribeProductsStore.nextDate &&
              t('next_delivery', { date: subscribeProductsStore.nextDate })
            "
            :discount="SUBSCRIPTION_DISCOUNT_VALUE"
            mode="subscription"
          />
        </div>

        <span class="orderProductCart__subscribeHint">
          {{ t('subscription_hint') }}
        </span>
      </BaseCollapse>

      <OrderCouponForm class="orderProductCart__coupon" />

      <OrderPaymentButton class="orderProductCart__buy" />
    </div>
  </div>

  <ClientOnly>
    <LazyProductSubscriptionInfoModal v-model:visible="isInfoModalVisible" />
  </ClientOnly>
</template>

<i18n>
ru:
  header: Ваш заказ
  title: Продукты
  products: 'нет продуктов | {n} продукт | {n} продукта | {n} продуктов'
  empty: У вас сейчас нет товаров в корзине
  cell:
    price: Общая стоимость
    discount: Скидка
    delivery: Доставка
    total: ИТОГО
    formatted_discount: -{value}
  subscription_benefit: Ваша подписка на рефил
  subscription_hint: Заказ по подписке будет сформирован отдельно, мы пришлем вам ссылку на оплату после подтверждения по телефону
  question: Как это работает?
  next_delivery: 'Приблизительная дата доставки: {date}'
  delete_product_confirmation: Вы уверены, что хотите убрать продукт из корзины?
  delete_subscription_confirmation: Вы уверены, что хотите убрать подписку из корзины?

en:
  header: Your order
  title: Products
  products: 'empty product | {n} product | {n} products | {n} products'
  empty: You currently have no items in your cart
  cell:
    price: Total cost
    discount: Discount
    delivery: Delivery
    total: TOTAL
    formatted_discount: -{value}
  subscription_benefit: Your subscription to the refil
  subscription_hint: The subscription order will be prepared separately, and we’ll send you a payment link once it’s confirmed over the phone.
  question: How does it work?
  next_delivery: 'Next delivery: {date}'
  delete_product_confirmation: Are you sure you want to remove the product from the cart?
  delete_subscription_confirmation: Are you sure you want to remove the subscription from the cart?
</i18n>

<style lang="scss">
.orderProductCart {
  padding: 120px 0;

  @include mq('md') {
    padding: 0;
    position: fixed;
    width: 100%;
    height: calc(100svh - 96px);
    background-color: #fff;
    display: flex;
    flex-direction: column;
    bottom: 0;
    z-index: 2;
    transform: translateY(calc(100% - 46px));
    transition: transform 0.5s ease;
  }

  &_visible {
    transform: translateY(0);
  }

  &__overlay {
    display: none;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #121212;
    opacity: 0.4;
    z-index: 1;

    @include mq('md') {
      display: block;
    }

    &.overlay-enter-active,
    &.overlay-leave-active {
      transition: opacity 0.5s ease !important;
    }

    &.overlay-enter-from,
    &.overlay-leave-to {
      opacity: 0;
    }
  }

  &__controller {
    height: 47px;
    border: none;
    border-top: 1px solid #e6e6e6;
    border-bottom: 1px solid #e6e6e6;
    padding: 0 16px;
    display: none;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    cursor: pointer;
    background: none;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #121212;

    @include mq('md') {
      display: flex;
    }
  }

  &__content {
    width: 100%;
    max-width: 506px;
    margin: 0 auto;
    padding: 0 20px;

    @include mq('md') {
      padding-top: 24px;
      padding-bottom: 42px;
      flex: 1;
      overflow: auto;
      max-width: initial;
    }
  }

  &__empty {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.2px;
    color: #121212;
    margin: 0;
    text-align: center;
    padding: 36px 0;
  }

  &__header {
    padding: 1px 0 20px;
    border-bottom: 1px solid #121212;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #121212;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__title {
    text-transform: uppercase;

    &_subscribe {
      @include mq('md') {
        font-size: 14px;
        line-height: 20px;
      }
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 0 48px;

    @include mq('md') {
      padding-bottom: 36px;
    }

    &_subscribe {
      padding-bottom: 0;
    }
  }

  &__subscribe {
    margin-bottom: 96px;

    @include mq('md') {
      margin-top: 64px;
      margin-bottom: 0;
    }
  }

  &__question {
    display: block;
    background: none;
    padding: 0;
    border: none;
    color: #121212;
    width: fit-content;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.2px;
    text-decoration: underline;
    text-underline-offset: 4px;
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.7;
    }

    @include mq('sm') {
      font-size: 12px;
      margin-top: auto;
    }
  }

  &__info {
    padding: 21px 0 22px;
    border-bottom: 1px solid #121212;
    border-top: 1px solid #121212;
    display: flex;
    flex-direction: column;
    gap: 15px;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #121212;
  }

  &__row {
    display: flex;
    justify-content: space-between;
  }

  &__total {
    padding-top: 24px;
    margin-bottom: 96px;
    font-size: 18px;
    line-height: 24px;
    display: flex;
    justify-content: space-between;
    letter-spacing: 0.2px;
    color: #121212;

    @include mq('md') {
      margin-bottom: 0;
    }
  }

  &__subscribeHint {
    display: block;
    color: #c0c0c0;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
    margin-top: 14px;

    @include mq('md') {
      font-size: 12px;
      line-height: 16px;
      margin-top: 16px;
    }
  }

  & &__coupon {
    @include mq('md') {
      display: none;
    }
  }

  & &__buy {
    margin-top: 24px;

    @include mq('md') {
      display: none;
    }
  }
}
</style>
